import { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, FolderOutput } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { useProjects } from '@/hooks/use-projects';
import { useLists } from '@/hooks/use-lists';
import { useListStatuses } from '@/hooks/use-lists';
import { useMoveTaskTo, MoveTaskToResult } from '@/hooks/use-tasks';
import { useMyCapabilities } from '@/hooks/use-permissions';
import type { Task } from '@/types';

interface MoveTaskDialogProps {
  task: Task;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMoved?: (result: { lostAccess: boolean; targetProjectId: string; targetListId: string }) => void;
}

export function MoveTaskDialog({ task, open, onOpenChange, onMoved }: MoveTaskDialogProps) {
  const { data: projects = [] } = useProjects();
  const { data: capabilities } = useMyCapabilities();
  const moveTaskTo = useMoveTaskTo();

  const [selectedProjectId, setSelectedProjectId] = useState(task.projectId);
  const [selectedListId, setSelectedListId] = useState('');
  const [selectedStatusId, setSelectedStatusId] = useState('');

  const { data: lists = [], isLoading: listsLoading } = useLists(selectedProjectId);
  const { data: statuses = [], isLoading: statusesLoading } = useListStatuses(selectedListId || undefined);

  const canMoveWithinProject = Boolean(capabilities?.['task.move_list']);
  const canMoveAcrossProjects = Boolean(capabilities?.['task.move_project']);

  const isSameProject = selectedProjectId === task.projectId;
  const isSameList = selectedListId === task.listId;
  const canMove = isSameProject ? canMoveWithinProject : canMoveAcrossProjects;

  const isValid = selectedProjectId && selectedListId && selectedStatusId && !isSameList;

  const willWarn = !isSameProject;

  useEffect(() => {
    if (open) {
      setSelectedProjectId(task.projectId);
      setSelectedListId('');
      setSelectedStatusId('');
    }
  }, [open, task.projectId]);

  useEffect(() => {
    setSelectedListId('');
    setSelectedStatusId('');
  }, [selectedProjectId]);

  useEffect(() => {
    setSelectedStatusId('');
  }, [selectedListId]);

  useEffect(() => {
    if (statuses.length > 0 && !selectedStatusId) {
      const openStatus = statuses.find((s) => !s.isDoneState);
      setSelectedStatusId(openStatus?.id || statuses[0].id);
    }
  }, [statuses, selectedStatusId]);

  const handleMove = async () => {
    if (!isValid || !canMove) return;

    try {
      const result = await moveTaskTo.mutateAsync({
        id: task.id,
        sourceProjectId: task.projectId,
        targetProjectId: selectedProjectId,
        targetListId: selectedListId,
        targetStatusId: selectedStatusId,
      });

      const moveResult = result as MoveTaskToResult;
      const lostAccess = Boolean(moveResult?.lostAccess);

      toast({
        title: lostAccess
          ? 'Task moved (you no longer have access)'
          : 'Task moved successfully',
      });

      onOpenChange(false);
      onMoved?.({
        lostAccess,
        targetProjectId: selectedProjectId,
        targetListId: selectedListId,
      });
    } catch (error) {
      toast({
        title: 'Failed to move task',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const availableLists = lists.filter((l) => l.id !== task.listId || selectedProjectId !== task.projectId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOutput className="h-5 w-5" />
            Move Task
          </DialogTitle>
          <DialogDescription>
            Move "{task.title}" to a different list or project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Project</Label>
            <Select
              value={selectedProjectId}
              onValueChange={setSelectedProjectId}
              disabled={!canMoveAcrossProjects && projects.length > 1}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                    {project.id === task.projectId && ' (current)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!canMoveAcrossProjects && projects.length > 1 && (
              <p className="text-xs text-muted-foreground">
                You don't have permission to move tasks between projects.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>List</Label>
            <Select
              value={selectedListId}
              onValueChange={setSelectedListId}
              disabled={listsLoading || availableLists.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder={listsLoading ? 'Loading...' : 'Select list'} />
              </SelectTrigger>
              <SelectContent>
                {availableLists.map((list) => (
                  <SelectItem key={list.id} value={list.id}>
                    {list.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={selectedStatusId}
              onValueChange={setSelectedStatusId}
              disabled={statusesLoading || !selectedListId || statuses.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder={statusesLoading ? 'Loading...' : 'Select status'} />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status.id} value={status.id}>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: status.color }}
                      />
                      {status.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {willWarn && selectedListId && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-yellow-500/10 border border-yellow-500/20 text-yellow-200 text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Moving to a different project will clear the task's current assignees
                since they may not have access to the target project.
              </span>
            </div>
          )}

          {!canMove && selectedListId && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {isSameProject
                  ? "You don't have permission to move tasks within this project."
                  : "You don't have permission to move tasks between projects."}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleMove}
            disabled={!isValid || !canMove || moveTaskTo.isPending}
          >
            {moveTaskTo.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Moving...
              </>
            ) : (
              'Move Task'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
